import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';

import * as EditProductionActions from 'Pages/manageproduction/state/editproduction/actions';
import * as EditProductionSelectors from 'Pages/manageproduction/state/editproduction/selectors';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import {
  PlannedSectionGridFields,
  OfferedSectionGridFields,
  ServedSectionGridFields,
  LeftoverFieldsMap,
  RowHeightOffset,
  RecipeGridElementIndex,
  PlannedGridElementIndex,
  OfferedGridElementIndex,
  ServedGridElementIndex,
  TemperatureGridElementIndex,
} from 'Pages/manageproduction/helpers/Constants';
import { getInputChangePayload } from 'Pages/manageproduction/helpers/form';
import * as ProdRecordHelpers from 'Pages/manageproduction/helpers/productionRecord';

import { MealSection } from './MealSection';
import { TemperatureSection } from './TemperatureSection';
import { PlannedSectionRow } from './PlannedSectionRow';
import { PlannedSectionHeaderRow } from './PlannedSectionHeaderRow';
import { OfferedSectionRow } from './OfferedSectionRow';
import { OfferedSectionHeaderRow } from './OfferedSectionHeaderRow';
import { ServedSectionRow } from './ServedSectionRow';
import { ServedSectionHeaderRow } from './ServedSectionHeaderRow';
import { PlannedSection } from './PlannedSection';
import { OfferedSection } from './OfferedSection';
import { ServedSection } from './ServedSection';

class ProductionGrid extends PureComponent {
  static propTypes = {
    viewProdRecordIndex: PropTypes.number.isRequired,
    isReadOnly: PropTypes.bool.isRequired,
    hookDispatch: PropTypes.func.isRequired,
    prodDetails: PropTypes.array.isRequired,
  };

  static defaultProps = {
    viewProdRecordIndex: 0,
  };

  state = {
    showStickyMealSection: false,
    stickyMealSectionStyles: {},
  };

  static get plannedSectionColumns() {
    return [
      { label: 'Reimbursable Meals', width: 80 },
      { label: '%', width: 100 },
      { label: 'Pre-K', width: 80 },
      { label: 'A La Carte', width: 80 },
      { label: 'Adult', width: 80 },
      { label: 'Planned Total', width: 80 },
      { label: 'Ingredient Amount', width: 80 },
      { label: 'Kitchen', width: 50 },
    ];
  }

  static get collapsedPlannedSectionColumns() {
    return ProductionGrid.plannedSectionColumns.slice(0, 1);
  }

  static get offeredSectionColumns() {
    return [
      { label: 'Reimbursable Offered', width: 80 },
      { label: 'Pre-K', width: 80 },
      { label: 'A La Carte', width: 80 },
      { label: 'Adult', width: 80 },
      { label: 'Offered Total', width: 80 },
    ];
  }

  static get collapsedOfferedSectionColumns() {
    return ProductionGrid.offeredSectionColumns.slice(0, 1);
  }

  static get servedSectionColumns() {
    return [
      { label: 'Total Served', width: 80 },
      { label: 'Pre-K', width: 80 },
      { label: 'A La Carte', width: 80 },
      { label: 'Adult', width: 80 },
      { label: 'Left Over', width: 80 },
      { label: 'Discard', width: 80 },
      { label: 'Reimbursable Served', width: 80 },
      { label: 'Available to Re-serve', width: 70 },
      { label: 'Serving Line #', width: 248 },
    ];
  }

  static get collapsedServedSectionColumns() {
    return ProductionGrid.servedSectionColumns.slice(0, 1);
  }

  mealSectionRef = React.createRef();
  gridRef = React.createRef();

  updateStickyMealSectionPosition = (scrollLeft) => {
    let width = 0;

    if (this.mealSectionRef.current) {
      width = this.mealSectionRef.current.offsetWidth;
    }

    const entreeNameWidth = ((width - 100) * 2) / 3;

    if (scrollLeft > entreeNameWidth) {
      this.setState({
        showStickyMealSection: true,
        stickyMealSectionStyles: {
          left: scrollLeft,
          height: this.stickyMealSectionHeight,
        },
      });
    } else {
      this.setState({
        showStickyMealSection: false,
        stickyMealSectionStyles: {},
      });
    }
  };

  onContainerScroll = (e) => {
    const { target } = e;

    this.updateStickyMealSectionPosition(target.scrollLeft);
  };

  get stickyMealSectionHeight() {
    let height = 0;

    if (this.mealSectionRef.current) {
      height = this.mealSectionRef.current.offsetHeight;
    }

    return height;
  }

  get commonSectionProps() {
    const {
      isReadOnly,
      viewProdRecordIndex,
      detailsByRecipeCategory,
      hookDispatch,
    } = this.props;

    return {
      isReadOnly,
      viewProdRecordIndex,
      detailsByRecipeCategory,
      hookDispatch,
      onInputChange: this.onInputChange,
    };
  }

  onInputChange = (e) => {
    const { hookDispatch, prodDetails, updateStoreLeftovers } = this.props;

    let action = getInputChangePayload(e);
    const {
      payload: { index, name, value },
    } = action;

    // Check if its a leftover field that will require updating leftovers
    if (
      !_.isNil(index) &&
      !_.isNil(prodDetails[index]) &&
      LeftoverFieldsMap[name]
    ) {
      const detail = prodDetails[index];

      if (ProdRecordHelpers.isLeftover(detail)) {
        const leftoverAmount = updateStoreLeftovers(detail, name, value);
        action.payload.value = leftoverAmount;
      }
    }

    hookDispatch(action);
  };

  componentDidUpdate() {
    const mealRows = [];

    // Update the Row heights to match the heights of the recipe rows which grow due to text wrapping
    if (this.gridRef.current) {
      //Get the grid row element's offsetHeight for each of the recipe rows and their index
      let count = 0;
      this.gridRef.current.childNodes[0].childNodes[
        RecipeGridElementIndex
      ].childNodes.forEach((e) => {
        if (e.className === 'grid-row') {
          mealRows.push({ offsetHeight: e.offsetHeight, index: count });
        }
        count += 1;
      });

      //iterate through each of the meal row objects and update the height for the planned, offered, served, and temperature grid rows based on the meal rows's offset height
      mealRows.forEach((elementObject) => {
        //planned row
        this.gridRef.current.childNodes[0].childNodes[
          PlannedGridElementIndex
        ].childNodes[elementObject.index].style.height = `${
          elementObject.offsetHeight - RowHeightOffset
        }px`;

        //offered row
        this.gridRef.current.childNodes[0].childNodes[
          OfferedGridElementIndex
        ].childNodes[elementObject.index].style.height = `${
          elementObject.offsetHeight - RowHeightOffset
        }px`;

        //served row
        this.gridRef.current.childNodes[0].childNodes[
          ServedGridElementIndex
        ].childNodes[elementObject.index].style.height = `${
          elementObject.offsetHeight - RowHeightOffset
        }px`;

        //temperature row
        this.gridRef.current.childNodes[0].childNodes[
          TemperatureGridElementIndex
        ].childNodes[elementObject.index].style.height = `${
          elementObject.offsetHeight - RowHeightOffset
        }px`;
      });
    }
  }

  render() {
    const { showStickyMealSection, stickyMealSectionStyles } = this.state;

    return (
      <Fragment>
        <div
          className="ep-grid"
          onScroll={this.onContainerScroll}
          ref={this.gridRef}
        >
          <div className="ep-production-records">
            <MealSection
              ref={this.mealSectionRef}
              {...this.commonSectionProps}
            />
            {showStickyMealSection && (
              <MealSection
                sticky={true}
                {...this.commonSectionProps}
                style={stickyMealSectionStyles}
              />
            )}
            <PlannedSection
              columns={ProductionGrid.plannedSectionColumns}
              collapsedColumns={ProductionGrid.collapsedPlannedSectionColumns}
              gridFields={PlannedSectionGridFields}
              className="ep-planned-grid"
              title="Planned"
              RowComponent={PlannedSectionRow}
              HeaderComponent={PlannedSectionHeaderRow}
              disableEditingWhenLeftover={true}
              {...this.commonSectionProps}
            />
            <OfferedSection
              columns={ProductionGrid.offeredSectionColumns}
              collapsedColumns={ProductionGrid.collapsedOfferedSectionColumns}
              className="ep-offered-grid"
              title="Offered"
              gridFields={OfferedSectionGridFields}
              RowComponent={OfferedSectionRow}
              HeaderComponent={OfferedSectionHeaderRow}
              {...this.commonSectionProps}
            />
            <ServedSection
              columns={ProductionGrid.servedSectionColumns}
              collapsedColumns={ProductionGrid.collapsedServedSectionColumns}
              className="ep-served-grid"
              title="Served"
              gridFields={ServedSectionGridFields}
              RowComponent={ServedSectionRow}
              HeaderComponent={ServedSectionHeaderRow}
              {...this.commonSectionProps}
            />
            <TemperatureSection {...this.commonSectionProps} />
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  viewProdRecordIndex: EditProductionSelectors.getViewProdRecordIndex(state),
});

const mapDispatchToProps = {
  setNotificationDialog,
  toggleShowIngredients: EditProductionActions.toggleShowIngredients,
  updateStoreLeftovers: EditProductionActions.updateStoreLeftovers,
};

ProductionGrid = connect(mapStateToProps, mapDispatchToProps, null, {
  withRef: true,
})(ProductionGrid);

export { ProductionGrid };



// WEBPACK FOOTER //
// ./src/pages/manageproduction/subpages/editproduction/components/ProductionGrid.js